import constate from 'constate';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Image } from 'react-native';

import { PlayerViewerType } from '..';
import { FinishLessonAction } from '../interfaces/finish-lesson-action';
import type { LessonInfo } from '../interfaces/lesson-info';

interface ChatBotLessonInfo {
  elementId?: number;
  activityPayload?: Record<string, unknown>;
  firstActivityPayload?: Record<string, unknown>;
  name?: string;
}

interface useActivityParams {
  slides: any[];
  rawData?: Record<string, unknown>;
  bookmark?: number;
  finishLesson?: (action?: FinishLessonAction) => void;
  redirectToReferralPage?: (action?: FinishLessonAction) => void;
  lessonInfo?: LessonInfo;
  onChatBotClick?: (info: ChatBotLessonInfo) => void;
  viewerType?: PlayerViewerType;
}

export function useActivity({
  slides: originalSlides,
  bookmark: unsafeBookmark = 0,
  finishLesson: _finishLesson,
  redirectToReferralPage: _redirectToReferralPage,
  lessonInfo,
  rawData,
  onChatBotClick,
  viewerType,
}: useActivityParams) {
  const [activityVisibility, setActivityVisibility] = useState(true);
  const [nextMovement, setNextMovement] = useState<'right' | 'left'>();

  const slides = useMemo(() => {
    const finishActivity = { name: 'Finish', type: 'SectionComplete' };
    return [...originalSlides, finishActivity];
  }, [originalSlides]);

  const bookmark = useMemo(() => {
    if (!slides || unsafeBookmark < 0 || unsafeBookmark >= slides.length) {
      return 0;
    }

    return unsafeBookmark;
  }, [unsafeBookmark, slides]);

  const finishLesson = useCallback(
    (action: FinishLessonAction = FinishLessonAction.FINISH) => {
      if (_finishLesson) {
        _finishLesson(action);
      }
    },
    [_finishLesson]
  );

  const redirectToReferralPage = useCallback(() => {
    if (_redirectToReferralPage) {
      _redirectToReferralPage(FinishLessonAction.REFERRAL);
    }
  }, [_redirectToReferralPage]);

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const nextLesson = slides[currentIndex + 1];
    if (nextLesson?.image) {
      Image.prefetch(nextLesson.image).catch(() => {});
    }
  }, [currentIndex, slides]);

  const currentActivity = useMemo(
    () => slides[currentIndex],
    [slides, currentIndex]
  );

  const currentSteps: any[] = useMemo(
    () => currentActivity?.steps ?? [],
    [currentActivity]
  );
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const currentStep = useMemo(
    () => currentSteps?.[currentStepIndex],
    [currentSteps, currentStepIndex]
  );

  const [completions, setCompletions] = useState<boolean[]>([]);
  const totalActivitiesCompleted = completions.filter((c) => !!c).length;
  const [stepsCompletions, setStepsCompletions] = useState<number[][]>([]);
  const currentTotalStepsCompleted =
    stepsCompletions[currentIndex]?.filter((c) => c === 1).length ?? 0;

  const isStepComplete = (stepIndex: number) =>
    !!stepsCompletions[currentIndex]?.[stepIndex];
  const isCurrentStepComplete = isStepComplete(currentStepIndex);
  const isCurrentActivityComplete = !!completions[currentIndex];

  useEffect(() => {
    const fillCompletions = () => {
      const completions = new Array(slides.length)
        .fill(false)
        .fill(true, 0, bookmark);

      setCompletions(completions);
    };

    const fillStepsCompletions = () => {
      const completions = slides.map(
        (slide, index) =>
          slide.steps?.map(() => (index < bookmark ? 1 : 0)) ?? null
      );
      setStepsCompletions(completions);
    };

    const setCurrentIndexWithBookmark = () => {
      setCurrentIndex(bookmark);
    };

    setCurrentIndexWithBookmark();
    fillCompletions();
    fillStepsCompletions();
  }, [slides, bookmark]);

  const completeStep = (stepIndex: number, isCorrect = true) => {
    const currentStepsCompletions = stepsCompletions[currentIndex]?.concat([]);
    if (!currentStepsCompletions) {
      return;
    }
    currentStepsCompletions[stepIndex] = isCorrect ? 1 : -1;
    const completions = [
      ...stepsCompletions.slice(0, currentIndex),
      currentStepsCompletions,
      ...stepsCompletions.slice(currentIndex + 1),
    ];

    const hasCompletedAllSteps = currentStepsCompletions.every((c) => c === 1);
    setStepsCompletions(completions);

    if (hasCompletedAllSteps) {
      completeCurrentActivity();
    }
  };

  const completeCurrentStep = (isCorrect = true) => {
    completeStep(currentStepIndex, isCorrect);
  };

  const completeAllStepsInCurrentActivity = () => {
    setStepsCompletions((completions) => {
      if (!completions[currentIndex]) {
        return completions;
      }

      const stepsCompletions = completions[currentIndex].map(() => 1);
      return [
        ...completions.slice(0, currentIndex),
        stepsCompletions,
        ...completions.slice(currentIndex + 1),
      ];
    });
  };

  const resetAllStepsForIndex = (index) => {
    setStepsCompletions((completions) => {
      if (!completions[index]) {
        return completions;
      }

      const stepsCompletions = completions[index].map(() => 0);
      return [
        ...completions.slice(0, index),
        stepsCompletions,
        ...completions.slice(index + 1),
      ];
    });
  };

  const completeCurrentActivity = () => {
    if (isCurrentActivityComplete) {
      return;
    }

    completeAllStepsInCurrentActivity();

    setCompletions((list) => {
      const completions = [
        ...list.slice(0, currentIndex),
        true,
        ...list.slice(currentIndex + 1),
      ];
      return completions;
    });
  };

  const goNextActivity = useCallback(() => {
    if (currentIndex >= totalActivitiesCompleted) {
      return;
    }

    setActivityVisibility(true);

    const totalActivities = slides.length;

    if (completions[currentIndex] === false) {
      return;
    }

    if (currentIndex >= totalActivities - 1) {
      finishLesson();
    } else {
      resetAllStepsForIndex(currentIndex + 1);
      setCurrentStepIndex(0);
      setCurrentIndex(currentIndex + 1);
    }
  }, [
    completions,
    currentIndex,
    finishLesson,
    slides,
    totalActivitiesCompleted,
  ]);

  const goPrevActivity = useCallback(() => {
    if (currentIndex <= 0) {
      return;
    }

    setActivityVisibility(true);

    resetAllStepsForIndex(currentIndex - 1);
    setCurrentStepIndex(0);
    setCurrentIndex(currentIndex - 1);
  }, [currentIndex]);

  const goNextStep = () => {
    const currentStepsCompletions = stepsCompletions[currentIndex];

    if (!currentStepsCompletions) {
      return;
    }

    const findNextStep = () => {
      let i = (currentStepIndex + 1) % currentSteps.length;
      while (i !== currentStepIndex) {
        if (currentStepsCompletions[i] !== 1) {
          return i;
        }
        i = (i + 1) % currentSteps.length;
      }

      if (currentStepsCompletions[i] !== 1) {
        return i;
      }

      return -1;
    };

    const nextStepIndex = findNextStep();

    if (nextStepIndex !== -1) {
      setCurrentStepIndex(nextStepIndex);
    } else {
      setActivityVisibility(false);
    }
  };

  const goNextWithoutSteps = useCallback(() => {
    if (currentIndex >= totalActivitiesCompleted) {
      return;
    }

    setActivityVisibility(false);
    setNextMovement('right');
  }, [totalActivitiesCompleted, currentIndex]);

  const goPrevWithoutSteps = useCallback(() => {
    if (currentIndex <= 0) {
      return;
    }

    setActivityVisibility(false);
    setNextMovement('left');
  }, [currentIndex]);

  const goNext = () => {
    const hasSteps = Array.isArray(currentSteps) && currentSteps.length > 0;

    if (hasSteps) {
      goNextStep();
    } else {
      goNextWithoutSteps();
    }
  };

  const goToActivityWithIndex = (index: number = 0, step: number = 0) => {
    if (currentIndex === index) {
      return;
    }

    if (!completions.slice(0, index).every((c) => c)) {
      return;
    }

    if (isCurrentActivityComplete) {
      completeAllStepsInCurrentActivity();
    }

    resetAllStepsForIndex(index);
    setCurrentIndex(index);
    setCurrentStepIndex(step);
  };

  const handleChatBotClick = useCallback(() => {
    const currentRawActivity = rawData?.slides?.[currentIndex];
    const firstRawActivity = rawData?.slides?.[0];

    onChatBotClick?.({
      activityPayload: currentRawActivity,
      elementId: currentActivity?.elementId,
      firstActivityPayload: firstRawActivity,
      name: currentActivity?.name,
    });
  }, [
    currentActivity?.elementId,
    currentActivity?.name,
    currentIndex,
    onChatBotClick,
    rawData?.slides,
  ]);

  const totalSlides = originalSlides?.length ?? 0;

  return {
    totalSlides,
    currentIndex,
    currentActivity,
    isCurrentActivityComplete,
    currentSteps,
    currentStep,
    currentStepIndex,
    goNext,
    goNextWithoutSteps,
    finishLesson,
    redirectToReferralPage,
    goNextActivity,
    completeCurrentActivity,
    completeCurrentStep,
    completeStep,
    isStepComplete,
    completions,
    stepsCompletions,
    isCurrentStepComplete,
    totalActivitiesCompleted,
    currentTotalStepsCompleted,
    goToActivityWithIndex,
    activityVisibility,
    lessonInfo,
    goPrevWithoutSteps,
    nextMovement,
    goPrevActivity,
    handleChatBotClick,
    viewerType,
  };
}

export const [ActivityProvider, useActivityContext] = constate(useActivity);
